import React from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, Search, Tags, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageState } from "@/components/common/PageState";
import type { AdminPartnerCategoryGroup } from "@/types/admin-partner-category-group";
import {
  useCreatePartnerCategoryGroup,
  useDeletePartnerCategoryGroup,
  usePartnerCategoryGroups,
  useSetPartnerCategoryGroupTags,
  useUnassignedPartnerCategoryTags,
  useUpdatePartnerCategoryGroup,
} from "../hooks/partner-category-groups.hooks";
import {
  CATEGORY_GROUP_LOCALES,
  type CategoryGroupNamesForm,
  type CategoryTagEntry,
  buildCategoryTagCatalog,
  groupTagIdsWith,
  groupTagIdsWithout,
  hasEnglishName,
  movesFromAnotherGroup,
  namesFromForm,
  namesToForm,
  searchCategoryTags,
} from "../utils/categoryGroups";

const TAGS_PREVIEW = 3;
const SEARCH_RESULTS_LIMIT = 50;

type TranslateFn = ReturnType<typeof useTranslation>["t"];

// ─── Group name and order ─────────────────────────────────────────────────────

interface CategoryGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: AdminPartnerCategoryGroup | null;
  isPending: boolean;
  onSubmit: (values: {
    names: Record<string, string>;
    sortOrder?: number;
  }) => Promise<void>;
}

const CategoryGroupDialog: React.FC<CategoryGroupDialogProps> = ({
  open,
  onOpenChange,
  group,
  isPending,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [names, setNames] = React.useState<CategoryGroupNamesForm>(() =>
    namesToForm(group?.names),
  );
  const [sortOrder, setSortOrder] = React.useState<string>(
    group ? String(group.sortOrder) : "",
  );
  const [showErrors, setShowErrors] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setNames(namesToForm(group?.names));
      setSortOrder(group ? String(group.sortOrder) : "");
      setShowErrors(false);
    }
  }, [open, group]);

  const filledNames = namesFromForm(names);
  const englishMissing = !hasEnglishName(filledNames);
  const parsedOrder = sortOrder.trim() === "" ? undefined : Number(sortOrder);
  const orderInvalid =
    parsedOrder !== undefined &&
    (!Number.isInteger(parsedOrder) || parsedOrder < 0);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (englishMissing || orderInvalid) {
      setShowErrors(true);
      return;
    }
    await onSubmit({ names: filledNames, sortOrder: parsedOrder });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {group
              ? t("partners.admin.categoryGroups.dialog.editTitle")
              : t("partners.admin.categoryGroups.dialog.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("partners.admin.categoryGroups.dialog.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {CATEGORY_GROUP_LOCALES.map((locale) => (
              <div key={locale} className="flex flex-col gap-1.5">
                <Label htmlFor={`category-group-name-${locale}`}>
                  {t(`partners.admin.categoryGroups.languages.${locale}`)}
                  {locale === "en" ? " *" : ""}
                </Label>
                <Input
                  id={`category-group-name-${locale}`}
                  value={names[locale]}
                  maxLength={80}
                  onChange={(event) =>
                    setNames((prev) => ({
                      ...prev,
                      [locale]: event.target.value,
                    }))
                  }
                />
                {locale === "en" && showErrors && englishMissing && (
                  <p className="text-destructive text-xs">
                    {t("partners.admin.categoryGroups.dialog.englishRequired")}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-1.5 sm:w-1/2">
            <Label htmlFor="category-group-sort-order">
              {t("partners.admin.categoryGroups.dialog.sortOrder")}
            </Label>
            <Input
              id="category-group-sort-order"
              type="number"
              min={0}
              step={1}
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {t("partners.admin.categoryGroups.dialog.sortOrderHelp")}
            </p>
            {showErrors && orderInvalid && (
              <p className="text-destructive text-xs">
                {t("partners.admin.categoryGroups.dialog.sortOrderInvalid")}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("partners.admin.categoryGroups.dialog.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? t("partners.admin.categoryGroups.dialog.saving")
                : t("partners.admin.categoryGroups.dialog.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Tags of one group ────────────────────────────────────────────────────────

interface CategoryGroupTagsDialogProps {
  group: AdminPartnerCategoryGroup | null;
  catalog: CategoryTagEntry[];
  canWrite: boolean;
  isPending: boolean;
  onClose: () => void;
  onChange: (tagIds: number[], move: boolean) => Promise<void>;
}

const CategoryGroupTagsDialog: React.FC<CategoryGroupTagsDialogProps> = ({
  group,
  catalog,
  canWrite,
  isPending,
  onClose,
  onChange,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    setSearch("");
  }, [group?.id]);

  if (!group) return null;

  const candidates = searchCategoryTags(catalog, search)
    .filter((entry) => entry.groupId !== group.id)
    .slice(0, SEARCH_RESULTS_LIMIT);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("partners.admin.categoryGroups.tagsDialog.title", {
              name: group.name,
            })}
          </DialogTitle>
          <DialogDescription>
            {t("partners.admin.categoryGroups.tagsDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <h3 className="text-foreground text-sm font-semibold">
            {t("partners.admin.categoryGroups.tagsDialog.current", {
              count: group.tags.length,
            })}
          </h3>
          {group.tags.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t("partners.admin.categoryGroups.tagsDialog.none")}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {group.tags.map((tag) => (
                <Badge key={tag.tagId} variant="secondary" className="gap-1">
                  {tag.name}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon_sm"
                    className="size-5"
                    disabled={!canWrite || isPending}
                    onClick={() =>
                      onChange(groupTagIdsWithout(group, tag.tagId), false)
                    }
                    aria-label={t(
                      "partners.admin.categoryGroups.tagsDialog.remove",
                      { name: tag.name },
                    )}
                  >
                    <X className="size-3.5" />
                  </Button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-foreground text-sm font-semibold">
            {t("partners.admin.categoryGroups.tagsDialog.addTitle")}
          </h3>
          <Input
            type="search"
            icon={<Search className="text-muted-foreground size-4" />}
            placeholder={t("partners.admin.categoryGroups.tagsDialog.search")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="flex max-h-72 flex-col overflow-y-auto rounded-md border">
            {candidates.length === 0 ? (
              <p className="text-muted-foreground p-3 text-sm">
                {t("partners.admin.categoryGroups.tagsDialog.noResults")}
              </p>
            ) : (
              candidates.map((entry) => {
                const move = movesFromAnotherGroup(group.id, entry);
                return (
                  <div
                    key={entry.tagId}
                    className="flex items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0"
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="text-foreground truncate text-sm">
                        {entry.name}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {entry.groupName
                          ? t(
                              "partners.admin.categoryGroups.tagsDialog.inGroup",
                              { name: entry.groupName, id: entry.tagId },
                            )
                          : t(
                              "partners.admin.categoryGroups.tagsDialog.inOther",
                              { id: entry.tagId },
                            )}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canWrite || isPending}
                      onClick={() =>
                        onChange(groupTagIdsWith(group, entry.tagId), move)
                      }
                    >
                      {move
                        ? t("partners.admin.categoryGroups.tagsDialog.move")
                        : t("partners.admin.categoryGroups.tagsDialog.add")}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t("partners.admin.categoryGroups.tagsDialog.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Tags outside every group ─────────────────────────────────────────────────

interface UnassignedTagsCardProps {
  catalog: CategoryTagEntry[];
  groups: AdminPartnerCategoryGroup[];
  canWrite: boolean;
  isPending: boolean;
  onAssign: (group: AdminPartnerCategoryGroup, tagId: number) => void;
}

const UnassignedTagsCard: React.FC<UnassignedTagsCardProps> = ({
  catalog,
  groups,
  canWrite,
  isPending,
  onAssign,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState("");
  const unassigned = catalog.filter((entry) => entry.groupId === null);
  const visible = searchCategoryTags(unassigned, search);

  return (
    <div className="flex flex-col gap-3 rounded-xl border p-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-foreground text-sm font-semibold">
          {t("partners.admin.categoryGroups.unassigned.title", {
            count: unassigned.length,
          })}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("partners.admin.categoryGroups.unassigned.description")}
        </p>
      </div>

      {unassigned.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t("partners.admin.categoryGroups.unassigned.empty")}
        </p>
      ) : (
        <>
          <Input
            type="search"
            icon={<Search className="text-muted-foreground size-4" />}
            placeholder={t("partners.admin.categoryGroups.unassigned.search")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="max-w-sm"
          />
          <div className="flex max-h-96 flex-col overflow-y-auto rounded-md border">
            {visible.map((entry) => (
              <div
                key={entry.tagId}
                className="flex items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="text-foreground truncate text-sm">
                    {entry.name}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    ID {entry.tagId}
                  </span>
                </div>
                <Select
                  value=""
                  disabled={!canWrite || isPending || groups.length === 0}
                  onValueChange={(groupId) => {
                    const group = groups.find((item) => item.id === groupId);
                    if (group) onAssign(group, entry.tagId);
                  }}
                >
                  <SelectTrigger className="w-56">
                    <SelectValue
                      placeholder={t(
                        "partners.admin.categoryGroups.unassigned.addTo",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Panel ────────────────────────────────────────────────────────────────────

const languagesOf = (group: AdminPartnerCategoryGroup): string =>
  Object.keys(group.names)
    .map((locale) => locale.toUpperCase())
    .join(", ");

const tagsPreview = (group: AdminPartnerCategoryGroup, t: TranslateFn) => {
  if (group.tags.length === 0) {
    return t("partners.admin.categoryGroups.table.noTags");
  }
  const names = group.tags.slice(0, TAGS_PREVIEW).map((tag) => tag.name);
  const rest = group.tags.length - names.length;
  return rest > 0
    ? `${names.join(", ")} ${t("partners.admin.categoryGroups.table.more", { count: rest })}`
    : names.join(", ");
};

export interface CategoryGroupsPanelProps {
  canWrite: boolean;
  /** The "Add group" action lives in the page header. */
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
}

/**
 * Partners → Categories (DV-159): the groups the guest filters activities by,
 * and the provider tags in each. Changes reach the guest filters at once.
 */
export const CategoryGroupsPanel: React.FC<CategoryGroupsPanelProps> = ({
  canWrite,
  createOpen,
  onCreateOpenChange,
}) => {
  const { t } = useTranslation();
  const groupsQuery = usePartnerCategoryGroups();
  const unassignedQuery = useUnassignedPartnerCategoryTags();
  const createGroup = useCreatePartnerCategoryGroup();
  const updateGroup = useUpdatePartnerCategoryGroup();
  const setGroupTags = useSetPartnerCategoryGroupTags();
  const deleteGroup = useDeletePartnerCategoryGroup();

  const [editing, setEditing] =
    React.useState<AdminPartnerCategoryGroup | null>(null);
  const [tagsGroupId, setTagsGroupId] = React.useState<string | null>(null);
  const [deleting, setDeleting] =
    React.useState<AdminPartnerCategoryGroup | null>(null);

  const groups = React.useMemo(
    () => groupsQuery.data ?? [],
    [groupsQuery.data],
  );
  const catalog = React.useMemo(
    () => buildCategoryTagCatalog(groups, unassignedQuery.data ?? []),
    [groups, unassignedQuery.data],
  );
  const tagsGroup = groups.find((group) => group.id === tagsGroupId) ?? null;

  const changeTags = async (
    group: AdminPartnerCategoryGroup,
    tagIds: number[],
    move: boolean,
  ) => {
    try {
      await setGroupTags.mutateAsync({
        id: group.id,
        data: { tagIds, move },
      });
      toast.success(t("partners.admin.categoryGroups.toasts.tagsSaved"));
    } catch {
      toast.error(t("partners.admin.categoryGroups.toasts.tagsError"));
    }
  };

  const saveGroup = async (values: {
    names: Record<string, string>;
    sortOrder?: number;
  }) => {
    try {
      if (editing) {
        await updateGroup.mutateAsync({ id: editing.id, data: values });
        setEditing(null);
      } else {
        await createGroup.mutateAsync(values);
        onCreateOpenChange(false);
      }
      toast.success(t("partners.admin.categoryGroups.toasts.saved"));
    } catch {
      toast.error(t("partners.admin.categoryGroups.toasts.saveError"));
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteGroup.mutateAsync(deleting.id);
      toast.success(t("partners.admin.categoryGroups.toasts.deleted"));
      setDeleting(null);
    } catch {
      toast.error(t("partners.admin.categoryGroups.toasts.deleteError"));
    }
  };

  if (groupsQuery.isLoading || unassignedQuery.isLoading) {
    return <PageState text={t("partners.admin.categoryGroups.loading")} />;
  }

  if (groupsQuery.isError || unassignedQuery.isError) {
    return (
      <PageState
        text={t("partners.admin.categoryGroups.loadError")}
        tone="error"
      />
    );
  }

  const columns: Column<AdminPartnerCategoryGroup>[] = [
    {
      key: "sortOrder",
      header: t("partners.admin.categoryGroups.table.order"),
      className: "w-[88px]",
    },
    {
      key: "name",
      header: t("partners.admin.categoryGroups.table.group"),
      render: (_value: string, row) => (
        <div className="flex flex-col">
          <span className="text-foreground font-medium">{row.name}</span>
          <span className="text-muted-foreground text-xs">{row.slug}</span>
        </div>
      ),
    },
    {
      key: "names",
      header: t("partners.admin.categoryGroups.table.languages"),
      render: (_value: unknown, row) => languagesOf(row),
    },
    {
      key: "tags",
      header: t("partners.admin.categoryGroups.table.tags"),
      className: "min-w-[280px] whitespace-normal",
      render: (_value: unknown, row) => (
        <div className="flex items-start gap-2">
          <Badge variant={row.tags.length ? "secondary" : "outline"}>
            {row.tags.length}
          </Badge>
          <span className="text-muted-foreground text-sm leading-5 break-words">
            {tagsPreview(row, t)}
          </span>
        </div>
      ),
    },
    {
      key: "actions",
      header: t("partners.admin.categoryGroups.table.actions"),
      className: "w-[132px] text-right",
      render: (_value: unknown, row) => (
        <div
          className="flex items-center justify-end"
          onClick={(event) => event.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={() => setTagsGroupId(row.id)}
            aria-label={t("partners.admin.categoryGroups.table.manageTags")}
            title={t("partners.admin.categoryGroups.table.manageTags")}
          >
            <Tags className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            disabled={!canWrite}
            onClick={() => setEditing(row)}
            aria-label={t("partners.admin.categoryGroups.table.edit")}
            title={t("partners.admin.categoryGroups.table.edit")}
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            disabled={!canWrite}
            onClick={() => setDeleting(row)}
            aria-label={t("partners.admin.categoryGroups.table.delete")}
            title={t("partners.admin.categoryGroups.table.delete")}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <p className="text-muted-foreground text-sm">
        {t("partners.admin.categoryGroups.description")}
      </p>

      <DataTable
        data={groups}
        columns={columns}
        emptyMessage={t("partners.admin.categoryGroups.table.empty")}
        getRowKey={(row) => row.id}
        withPagination={false}
        withSorting={false}
      />

      <UnassignedTagsCard
        catalog={catalog}
        groups={groups}
        canWrite={canWrite}
        isPending={setGroupTags.isPending}
        onAssign={(group, tagId) =>
          changeTags(group, groupTagIdsWith(group, tagId), false)
        }
      />

      <CategoryGroupDialog
        open={createOpen}
        onOpenChange={onCreateOpenChange}
        group={null}
        isPending={createGroup.isPending}
        onSubmit={saveGroup}
      />

      <CategoryGroupDialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        group={editing}
        isPending={updateGroup.isPending}
        onSubmit={saveGroup}
      />

      <CategoryGroupTagsDialog
        group={tagsGroup}
        catalog={catalog}
        canWrite={canWrite}
        isPending={setGroupTags.isPending}
        onClose={() => setTagsGroupId(null)}
        onChange={(tagIds, move) =>
          tagsGroup ? changeTags(tagsGroup, tagIds, move) : Promise.resolve()
        }
      />

      <Dialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("partners.admin.categoryGroups.deleteDialog.title", {
                name: deleting?.name ?? "",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("partners.admin.categoryGroups.deleteDialog.description", {
                count: deleting?.tags.length ?? 0,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleting(null)}
            >
              {t("partners.admin.categoryGroups.dialog.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteGroup.isPending}
              onClick={confirmDelete}
            >
              {t("partners.admin.categoryGroups.deleteDialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const CategoryGroupsAddButton: React.FC<{
  canWrite: boolean;
  onClick: () => void;
}> = ({ canWrite, onClick }) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="icon-text"
      disabled={!canWrite}
      onClick={onClick}
      title={
        canWrite
          ? undefined
          : t("common.insufficientPermissions", "Insufficient permissions")
      }
    >
      <Plus className="size-4" />
      {t("partners.admin.categoryGroups.add")}
    </Button>
  );
};
